import { Component } from '@theme/component';
import { onDocumentReady, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * The duration to wait for hiding animation, when sticky behavior is 'scroll-up'
   * @constant {number}
   */
  #animationDelay = 150;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry) return;

    const { height } = entry.target.getBoundingClientRect();
    document.body.style.setProperty('--header-height', `${height}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    const breakpoint = 1023; // Your mobile breakpoint
    const currentWidth = window.innerWidth;
    
    if (currentWidth <= breakpoint) {
      // Switch to mobile menu
      if (!this.refs.headerMenu.classList.contains('hidden')) {
        this.#updateMenuVisibility(true);
      }
    } else {
      // Switch to desktop menu
      if (this.refs.headerMenu.classList.contains('hidden')) {
        this.#updateMenuVisibility(false);
      }
    }
  });


  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      } else {
        this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
#handleOverflowMinimum = (event) => {
  this.#updateMenuVisibility(true); // Always activate mobile menu when overflow occurs
};

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }


  /**
   * WW - Sync announcement bar visibility with header scroll behavior.
   * Uses announcement bar section attribute: data-sticky="none|always|scroll-up"
   */
  #syncAnnouncementBars = () => {
    const bars = document.querySelectorAll('.announcement-bar[data-sticky]');
    if (!bars.length) return;

    const headerStickyMode = this.getAttribute('sticky') || '';
    const headerStickyState = this.dataset.stickyState || 'inactive';
    const headerDir = this.dataset.scrollDirection || 'none';

    bars.forEach((bar) => {
      const mode = bar.getAttribute('data-sticky') || 'none';

      // None -> no sticky behavior controlled here
      if (mode === 'none') {
        bar.removeAttribute('data-ww-hidden');
        bar.dataset.scrollDirection = 'none';
        return;
      }

      // Always -> always visible
      if (mode === 'always') {
        bar.removeAttribute('data-ww-hidden');
        bar.dataset.scrollDirection = 'none';
        return;
      }

      // Scroll up -> follow header behavior
      // Hide when scrolling down OR when header is in idle hidden state.
      // Show when scrolling up OR when header is inactive (natural position/top).
      const shouldHide =
        headerDir === 'down' ||
        headerStickyState === 'idle' ||
        (headerStickyMode === 'always' && headerDir === 'down');

      if (shouldHide) bar.setAttribute('data-ww-hidden', '1');
      else bar.removeAttribute('data-ww-hidden');

      // Optional: expose direction for CSS if needed
      bar.dataset.scrollDirection = headerDir;
    });
  };

#handleWindowScroll = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const announcementBar = document.querySelector('#announcement-bar-component');
    
    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      if (isScrollingUp) {
        if (this.getBoundingClientRect().top >= 0) {
          this.dataset.scrollDirection = 'none';
          if (announcementBar) announcementBar.dataset.scrollDirection = 'none';
        } else {
          this.dataset.scrollDirection = 'up';
          if (announcementBar) announcementBar.dataset.scrollDirection = 'up';
        }
      } else {
        this.dataset.scrollDirection = 'down';
        if (announcementBar) announcementBar.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      this.removeAttribute('data-animating');

      if (this.getBoundingClientRect().top >= 0) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.dataset.stickyState = 'inactive';
        this.dataset.scrollDirection = 'none';
        if (announcementBar) announcementBar.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.dataset.stickyState = 'active';
        this.dataset.scrollDirection = 'up';
        if (announcementBar) announcementBar.dataset.scrollDirection = 'up';
      }
    } else if (this.dataset.stickyState === 'active') {
      this.dataset.scrollDirection = 'none';
      if (announcementBar) announcementBar.dataset.scrollDirection = 'down';
      // delay transitioning to idle hidden state for hiding animation
      this.setAttribute('data-animating', '');

      this.#timeout = setTimeout(() => {
        this.dataset.stickyState = 'idle';
        this.removeAttribute('data-animating');
      }, this.#animationDelay);
    } else {
      this.dataset.scrollDirection = 'none';
      this.dataset.stickyState = 'idle';
      if (announcementBar) announcementBar.dataset.scrollDirection = 'down';
    }

    this.#syncAnnouncementBars();

    this.#lastScrollTop = scrollTop;
  };


  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    // --- WooWooLand FIX: Force desktop menu when window is wide enough ---
window.addEventListener(
  "resize",
  () => {
    const breakpoint = 1023;   // Same breakpoint as theme
    const width = window.innerWidth;

    if (width > breakpoint) {
      // Always show desktop menu
      this.refs.headerDrawerContainer.classList.add("desktop:hidden");
      this.refs.headerMenu.classList.remove("hidden");
    }
  },
  { passive: true }
);
// --- END FIX ---


    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        document.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentReady(() => {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver(() => calculateHeaderGroupHeight(header, headerGroup));

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      resizeObserver.observe(element);
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element === header || !(element instanceof HTMLElement)) continue;
            resizeObserver.observe(element);
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});

